import { useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES } from "../data/countriesList";

const ISO_LABEL = new Map(COUNTRIES.map((c) => [c.value, c.label]));

/**
 * Single-country predictive picker for admin (same behaviour as job search country search).
 */
export default function AdminCountryPicker({
  value,
  onChange,
  excludedCodes = [],
  disabled = false,
  inputId = "admin-country-picker",
  placeholder = "Search countries",
}) {
  const wrapRef = useRef(null);
  const [inputDraft, setInputDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const excluded = useMemo(() => {
    const set = new Set(excludedCodes.map((c) => String(c || "").trim().toUpperCase()).filter(Boolean));
    if (value) set.add(value);
    return set;
  }, [excludedCodes, value]);

  const q = inputDraft.trim().toLowerCase();

  const suggestions = useMemo(() => {
    const pool = COUNTRIES.filter((c) => !excluded.has(c.value));
    if (!q) return pool.slice(0, 12);
    return pool
      .filter((c) => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q))
      .slice(0, 12);
  }, [q, excluded]);

  useEffect(() => {
    setHighlight(0);
  }, [q]);

  useEffect(() => {
    function onDocDown(event) {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const pick = (iso) => {
    onChange(iso);
    setInputDraft("");
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setInputDraft("");
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (suggestions.length === 0) return;
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const match = suggestions[highlight];
      if (match) pick(match.value);
    } else if (event.key === "Escape") {
      setOpen(false);
    } else if (event.key === "Backspace" && !inputDraft && value) {
      clear();
    }
  };

  return (
    <div ref={wrapRef} className="jw-admin-country-picker">
      <div
        className={`jw-admin-country-picker__editor${disabled ? " is-disabled" : ""}`}
        role="group"
        aria-label="Country"
      >
        {value ? (
          <span className="jw-admin-country-picker__pill">
            <span className="jw-admin-country-picker__pill-label">{ISO_LABEL.get(value) || value}</span>
            <button
              type="button"
              className="jw-admin-country-picker__pill-remove"
              disabled={disabled}
              aria-label={`Remove ${ISO_LABEL.get(value) || value}`}
              onClick={clear}
            >
              ×
            </button>
          </span>
        ) : null}
        {!value ? (
          <input
            id={inputId}
            type="text"
            className="jw-admin-country-picker__input"
            disabled={disabled}
            value={inputDraft}
            onChange={(event) => {
              setInputDraft(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            aria-autocomplete="list"
            aria-expanded={open && suggestions.length > 0 ? "true" : "false"}
            aria-controls={`${inputId}-listbox`}
          />
        ) : null}
      </div>
      {open && !disabled && !value && suggestions.length > 0 ? (
        <ul id={`${inputId}-listbox`} className="jw-admin-country-picker__suggest" role="listbox">
          {suggestions.map((country, index) => (
            <li
              key={country.value}
              role="option"
              aria-selected={index === highlight}
              className={`jw-admin-country-picker__suggest-item${
                index === highlight ? " is-highlighted" : ""
              }`}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(country.value);
              }}
            >
              <span className="jw-admin-country-picker__suggest-label">{country.label}</span>
              <span className="jw-admin-country-picker__suggest-iso">{country.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
